import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IFailedPayload } from 'redux/types/Call.types';
import {
  IDownloadSampleDocumentRequest,
  IDownloadSampleDocumentResponse,
  IEditDocumentDueDateRequest,
  IEditDocumentResponse,
  IEditDocumentStatusRequest,
  IFolder,
  IGetAgencyDetailRequest,
  IGetAgencyDetailResponse,
  IGetDocumentsRequest,
  IGetDocumentsResponse,
  IGetFoldersRequest,
  IGetFoldersResponse,
  IShowDocumentRequest,
  IShowDocumentResponse,
  IUploadDocumentRequest,
  IUploadDocumentResponse,
} from 'redux/types/compliance/Document.types';
import { ComplianceTabType } from 'redux/types/compliance/index.types';

export interface DocumentState {
  error: IFailedPayload | null;
  loading: boolean;
  message: string;

  // TODO: Move this activeTab logic to common place
  activeTab: string;
  page: number;
  documents: IGetDocumentsResponse | null;
  agencyDetail: IGetAgencyDetailResponse | null;
  folders: IGetFoldersResponse | null;
  activeFolder: IFolder | null;
  activeDocument: IShowDocumentResponse | null;
}

export const documentInitialState: DocumentState = {
  error: null,
  loading: false,
  message: '',
  activeTab: ComplianceTabType.ALL_DOCUMENTS,
  page: 1,
  documents: null,
  agencyDetail: null,
  activeFolder: null,
  folders: null,
  activeDocument: null,
};

const documentSlice = createSlice({
  name: 'compliance/document',
  initialState: documentInitialState,
  reducers: {
    fetchDocuments: (state, action: PayloadAction<IGetDocumentsRequest>) => {
      state.loading = true;
      state.error = null;
    },

    fetchDocumentsSuccess: (
      state,
      action: PayloadAction<IGetDocumentsResponse>
    ) => {
      state.loading = false;
      state.documents = action.payload;
    },
    fetchDocumentsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    uploadDocument: (state, action: PayloadAction<IUploadDocumentRequest>) => {
      state.loading = true;
      state.error = null;
    },
    uploadDocumentSuccess: (
      state,
      action: PayloadAction<IUploadDocumentResponse>
    ) => {
      state.loading = false;
    },
    uploadDocumentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    showDocument: (state, action: PayloadAction<IShowDocumentRequest>) => {
      state.loading = true;
      state.error = null;
    },
    showDocumentSuccess: (
      state,
      action: PayloadAction<IShowDocumentResponse>
    ) => {
      state.loading = false;
      state.activeDocument = action.payload;
    },
    showDocumentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    downloadSampleDocument: (
      state,
      action: PayloadAction<IDownloadSampleDocumentRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    downloadSampleDocumentSuccess: (
      state,
      action: PayloadAction<IDownloadSampleDocumentResponse>
    ) => {
      state.loading = false;
    },
    downloadSampleDocumentFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    fetchAgencyDetail: (
      state,
      action: PayloadAction<IGetAgencyDetailRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchAgencyDetailSuccess: (
      state,
      action: PayloadAction<IGetAgencyDetailResponse>
    ) => {
      state.loading = false;
      state.agencyDetail = action.payload;
    },
    fetchAgencyDetailFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    editDocumentStatus: (
      state,
      action: PayloadAction<IEditDocumentStatusRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    editDocumentStatusSuccess: (
      state,
      action: PayloadAction<IEditDocumentResponse>
    ) => {
      state.loading = false;
    },
    editDocumentStatusFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    editDocumentDueDate: (
      state,
      action: PayloadAction<IEditDocumentDueDateRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    editDocumentDueDateSuccess: (
      state,
      action: PayloadAction<IEditDocumentResponse>
    ) => {
      state.loading = false;
    },
    editDocumentDueDateFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    fetchFolders: (state, action: PayloadAction<IGetFoldersRequest>) => {
      state.loading = true;
      state.error = null;
    },
    fetchFoldersSuccess: (
      state,
      action: PayloadAction<IGetFoldersResponse>
    ) => {
      state.loading = false;
      state.folders = action.payload;
    },
    fetchFoldersFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setActiveFolder: (state, action: PayloadAction<IFolder>) => {
      state.activeFolder = action.payload;
    },
  },
});

export const {
  fetchDocuments,
  fetchDocumentsSuccess,
  fetchDocumentsFailure,
  uploadDocument,
  uploadDocumentSuccess,
  uploadDocumentFailure,
  showDocument,
  showDocumentSuccess,
  showDocumentFailure,
  downloadSampleDocument,
  downloadSampleDocumentSuccess,
  downloadSampleDocumentFailure,
  fetchAgencyDetail,
  fetchAgencyDetailSuccess,
  fetchAgencyDetailFailure,
  editDocumentStatus,
  editDocumentStatusSuccess,
  editDocumentStatusFailure,
  editDocumentDueDate,
  editDocumentDueDateSuccess,
  editDocumentDueDateFailure,
  fetchFolders,
  fetchFoldersSuccess,
  fetchFoldersFailure,
  setActiveTab,
  setPage,
  setActiveFolder,
} = documentSlice.actions;

export default documentSlice.reducer;
